import { interaction, SizableText, XStack, YStack } from '@mythical/ui'
import { useCallback, useState } from 'react'

import {
  IconFaceSad,
  IconFaceHappy,
  IconFaceNeutral,
  IconFaceSadFilled,
  IconFaceHappyFilled,
  IconFaceNeutralFilled,
} from 'app/components/primitive/Icon'

export type RatingTypes = 'happy' | 'neutral' | 'sad'

const ratingIconMap: { [key in RatingTypes]: Icons } = {
  sad: IconFaceSad,
  neutral: IconFaceNeutral,
  happy: IconFaceHappy,
} as const

const ratingIconFilledMap: { [key in RatingTypes]: Icons } = {
  sad: IconFaceSadFilled,
  neutral: IconFaceNeutralFilled,
  happy: IconFaceHappyFilled,
} as const

type Icons = typeof IconFaceSad

type ContentRatingProps = {
  onRatingPress: (rating: number) => void
  currentRating?: number
}

export const ratingMap = {
  sad: -1,
  neutral: 0,
  happy: 1,
}

export const ratingMapReverse = {
  '-1': 'sad',
  '0': 'neutral',
  '1': 'happy',
}

const ContentRating = ({
  onRatingPress,
  currentRating,
}: ContentRatingProps) => {
  const [hasRated, setHasRated] = useState<RatingTypes | null>(
    ratingMapReverse[`${currentRating}`],
  )

  const onRatingPressWrapper = useCallback(
    (rating: RatingTypes) => {
      setHasRated(rating)
      const ratingNumber = ratingMap[rating]
      onRatingPress(ratingNumber)
    },
    [onRatingPress],
  )

  const currentRatingType = hasRated

  return (
    <XStack
      jc="center"
      ai="center"
      py="$2"
      px="$3"
      gap="$2"
      bg="$bgTertiary"
      br="$pill"
      h={44}
      $gtLg={{ px: '$5', py: '$3', h: 52 }}
    >
      <SizableText size="$3" fow="500">
        Rate This Video
      </SizableText>
      <XStack justifyContent="center" gap="$1.5">
        {Object.keys(ratingIconMap).map((rating) => {
          const ratingKey = rating as RatingTypes
          const isActive = ratingKey === currentRatingType
          return (
            <RatingButton
              key={rating}
              isActive={isActive}
              onPress={() => onRatingPressWrapper(ratingKey)}
              Icon={(isActive ? ratingIconFilledMap : ratingIconMap)[ratingKey]}
            />
          )
        })}
      </XStack>
    </XStack>
  )
}

export default ContentRating

type RatingButtonProps = {
  Icon: Icons
  isActive?: boolean
  onPress: () => void
}

const RatingButton = ({ Icon, onPress, isActive }: RatingButtonProps) => {
  return (
    <YStack
      tag="button"
      onPress={onPress}
      cursor="pointer"
      {...interaction.opacity}
      {...(isActive && {
        focusStyle: { opacity: 1 },
        hoverStyle: { opacity: 1 },
        boc: '$fgPrimary',
      })}
    >
      <Icon size={28} color={isActive ? 'white' : undefined} />
    </YStack>
  )
}
